import React, {Component} from 'react';
import {$, showSpinner, stopSpinner, ga} from "../lib/ext-refs";
import {camelCaseObject, sendGoogleAnalyticsEvent} from "../lib/utils";
import {
    errorToFieldMap,
    convertSubmitErrors,
    handleUnauthorizedRequest,
    generateUUID
} from "../lib/payment-utils";

/**
 * This component renders the Review Payment section.
 * It contains:
 * - Payment Transaction List table
 * - Total payment amount + convenience fee (if exists)
 * - Make payment button
 * - Logic for submitting the payment to rest xotp
 * Expected properties:
 * - active
 * - state
 * - appComponents
 */
class ReviewPayment extends Component {

    constructor(props) {
        super(props);
        this.stagePayment = this.stagePayment.bind(this);
        this.submitPayment = this.submitPayment.bind(this);
        this.launchStageDialog = this.launchStageDialog.bind(this);
    }

    mapSubmitErrors(errors) {
        const {state} = this.props;
        const {config} = state;
        const {messages} = config;
        // if we have errors from xotp, first try to map it
        // to a field so we can display the error under that field.
        // if the error cannot be mapped, then display the error
        // at the bottom as a global error
        let {globalErrors, mappedErrors} = convertSubmitErrors(errors, errorToFieldMap, state.fields);
        state.setErrors(mappedErrors);
        if (globalErrors.length === 0) {
            // All messages mapped, add message to highlight this
            globalErrors.push({
                code: 'invalidFields',
                message: messages['errorGeneral.label']
            });
        }
        // set global errors
        state.setValue('globalErrors', globalErrors);
    }

    stagePayment(e) {
        const {state, appComponents} = this.props;
        const {data, config, setValue, setValues} = state;
        const {nginxJwtToken} = data;
        const {tla, xotpUser, paymentConfig, messages} = config;
        const {preparePaymentForSubmission} = appComponents;

        sendGoogleAnalyticsEvent('Payment', messages["ga.event.action.makePayment"] || 'Clicked Make Payment', config.gaEventLabel);

        // reset the session timeout warning, set the flag to true so that session timeout warning will extend 
        // when make payment in AD.
        setValue('resetSessionTimeoutWarning', true);

        // revalidate fields
        // if there is error, issue state change, and bail out
        // if there is no error, execute ajax call
        // and issue state change from ajax callback.
        const validateResult = this.validatePayment(state, ()=>this.stagePayment());
        if (!validateResult) {
            // if validation failed, there should be error messages displayed
            // and abort submit payment
            return;
        }

        showSpinner(180000);

        let preparedPayment = preparePaymentForSubmission(data, config);

        // URL, token ...
        // do an Ajax call, and update state when received answer
        $.ajax({
            method: 'POST',
            url: `./xotp-api/stage/${tla}?JWT_TOKEN=${encodeURIComponent(nginxJwtToken)}`,
            username: xotpUser,
            timeout: (paymentConfig.xotpApiTimeout || 60) * 1000,
            contentType: 'application/json',
            data: JSON.stringify(preparedPayment),
            dataType: "json",
            success: (data) => {
                data = camelCaseObject(data) || {};
                let {paymentResponse} = data;

                if (paymentResponse.status === 'SUCCESS') {
                    setValue('stagedId', paymentResponse.stagedId);
                    if(paymentResponse.stagingGroupId){
                        setValue('stagingGroupId', paymentResponse.stagingGroupId);
                    }                    
                    this.launchStageDialog();
                    return;
                }
                let errors = paymentResponse.errors;
                if (errors) {
                    this.mapSubmitErrors(errors.error);
                }
            },
            error: (jqXHR, textStatus, errorThrown) => {
                if (jqXHR.status === 401) {
                    handleUnauthorizedRequest();
                    return;
                }
                // treat all errors as payment failure and move to receipt page
                setValues((data) => {
                    data.paymentResponse = {paymentStatus: "FAILED"};
                    data.showReceipt = true;
                    return data;
                });
            },
            complete: (xhr, status) => {
                stopSpinner();
                if (status === 'timeout') {
                    // show receipt with timeout
                    setValues((data) => {
                        data.paymentResponse = {paymentStatus: "TIMEOUT"};
                        data.showReceipt = true;
                        return data;
                    });
                }
            }
        });
    }

    launchStageDialog() {
        const {state} = this.props,
            {setValue} = state;
        setValue('launchStageDialog', true);
    }

    /**
     * Submits a payment to rest xotp.
     * @param e
     */
    submitPayment(e) {
        const {state, appComponents, onCardPresentInteraction, postProcessSuccessfulPayment} = this.props,
        {data, config, setValue, setValues} = state,
        {nginxJwtToken, paymentMethod} = data,
        {paymentMethodCategory} = paymentMethod,
        {tla, xotpUser, paymentConfig, messages} = config,
        {preparePaymentForSubmission} = appComponents;

        let requestData = data;

        if (requestData.paymentInProgress) {
            console.warn('Double click on submit payment detected, preventing duplicate submission');
            return;
        }

        sendGoogleAnalyticsEvent('Payment', messages["ga.event.action.makePayment"] || 'Clicked Make Payment', config.gaEventLabel);

        // revalidate fields
        // if there is error, issue state change, and bail out
        // if there is no error, execute ajax call
        // and issue state change from ajax callback.
        const validateResult = this.validatePayment(state, ()=>this.submitPayment());
        if (!validateResult) {
            // if validation failed, there should be error messages displayed
            // and abort submit payment
            return;
        }

        $('.spinner-text').html(messages["submittingPayments.label"]);
        if (paymentMethodCategory !== 'CP_POS') {
            showSpinner(180000);
        }

        // TODO submit multiple payments for multipay
        let preparedPayment = preparePaymentForSubmission(requestData, config);
        preparedPayment.payment["using-card-present-dedicated-flow"] = data.usingCardPresentDedicatedFlow || false;

        // payment is in progress, set the flag to true so we don't make same request again
        setValue('paymentInProgress', true);

        // reset the session timeout warning, set the flag to true so that session timeout warning will extend 
        // when make payment in AD.
        setValue('resetSessionTimeoutWarning', true);

        $.ajax({
            method: 'POST',
            // XOTP Url now relative to AD.
            url: `./xotp-api/payments/${tla}?JWT_TOKEN=${encodeURIComponent(nginxJwtToken)}`,
            username: xotpUser,
            password: "",
            headers: {
                "Content-Type": "application/json",
                "x-uuid": generateUUID()
            },
            timeout: (paymentConfig.xotpApiTimeout || 60) * 1000,
            dataType: "json",
            data: JSON.stringify(preparedPayment),
            success: (responseData) => {
                responseData = camelCaseObject(responseData);
                responseData = responseData.paymentResponse;
                setValue('paymentResponse', responseData);

                // if we have errors from xotp, first try to map it
                // to a field so we can display the error under that field.
                // if the error cannot be mapped, then display the error
                // at the bottom as a global error, also return so as
                // not to show receipt page
                let errors = responseData.errors;
                if (errors) {
                    this.mapSubmitErrors(errors.error);
                    return;
                }

                // With card-present payments, the initial XOTP call simply
                // creates a payment header, leaves it in PROGRESS state,
                // and returns a Paymentus reference ID. This is then used
                // when invoking the transaction through the configured
                // processor client specific to the card-present payment method.
                if (paymentMethodCategory === 'CP_POS') {
                    setValue('preparedPayment', preparedPayment);
                    onCardPresentInteraction();
                } 

                // Post payment processing
                postProcessSuccessfulPayment(responseData, requestData, state);
            },
            error: (jqXHR, textStatus, errorThrown) => {
                console.log('Error: ', jqXHR, textStatus, errorThrown);
                if (jqXHR.status === 401) {
                    handleUnauthorizedRequest();
                    return;
                }
                // treat all errors as payment failure and move to receipt page
                setValues((data) => {
                    data.paymentResponse = {paymentStatus: "FAILED"};
                    data.showReceipt = true;
                    return data;
                });
            },
            complete: (xhr, status) => {
                //payment is not in progress anymore set the payment in progress flag to false
                setValue('paymentInProgress', false)
                if (paymentMethodCategory !== 'CP_POS') {
                    stopSpinner();
                }
                if (status === 'timeout') {
                    // show receipt with timeout
                    setValues((data) => {
                        data.paymentResponse = {paymentStatus: "TIMEOUT"};
                        data.showReceipt = true;
                        return data;
                    });
                }

            }
        });

    }


    /**
     * Trigger validation on all input fields on the page and also
     * validate we have all necessary data to make a payment.
     * Modifies the state.
     *
     * @param state
     * @param restart call back function to run after all async validation is done
     * @returns {boolean}
     */
    validatePayment(state, restart) {
        /*
         Trigger validation of all registered fields 
         
         When async validators are present, this function will loop on itself which essentially polls the registry of 
         async validations still in process. When we get to this point, we do no want to re-trigger validation again 
        */
        if(!this.waitingForAsync) {
            state.validateAllSync();
        }
        
        // if there are async validations running, we want to wait for them to complete
        this.waitingForAsync = state.checkAsyncValidation();
        if(this.waitingForAsync){
            // If we are waiting, the restart() call below will call this function again 
            showSpinner(180000);
            setTimeout(()=>restart(), 2000);
            return false;
        } else {
            stopSpinner();
        }
        
        // check the validateState, if any field has status "error" then
        // we have errors
        const {validationState, data, config} = state;
        const {messages} = config;
        const errorFields = Object.entries(validationState).filter(([key, value]) => !!value);

        // start with no errors
        let globalErrors = [];
        if (errorFields.length > 0) {
            // return false, and setup a message to show
            globalErrors.push({
                code: 'invalidFields',
                message: messages['errorGeneral.label']
            });
        }
        // check for payment amount
        // now check if we have a payment amount or payment method
        if (data.totalPaymentAmount <= 0) {
            globalErrors.push({
                code: "paymentAmount.empty",
                message: "Please select an item to pay"
            });
        }
        state.setValues((data) => {
            data.globalErrors = globalErrors;
            return data;
        });
        return (globalErrors.length === 0);
    }

    render() {
        const {active, showIcon, state, appComponents} = this.props;
        const {config, data, setValue} = state;
        const {messages} = config;
        const pmCategory = data.paymentMethod.paymentMethodCategory;

        const {
            ReviewPaymentTransactionList,
            StagingReviewPayment,
            ReviewPaymentTotalAmount,
            ReviewPaymentButtons
        } = appComponents;

        const stagedFlow = StagingReviewPayment.isStagedPMCategory(pmCategory);
        return (
            <div className={"opp-section" + (!active ? " disable-section" : "")} id="ReviewPayment">
                <h3 className="section-title">
                    {showIcon && (
                        <span className="section-icon">
                            <img src={messages["opp.icon-review"] || "images/icon-review.png"} alt="Review Payment"/>
                        </span>)
                    }
                    <span className="section-text">{messages["opp.reviewPayment.title"]}</span>
                </h3>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-v2">
                            <div className="card-body">
                                <ReviewPaymentTransactionList data={data} config={config}/>
                                <ReviewPaymentTotalAmount data={data} config={config}/>
                                <StagingReviewPayment data={data} config={config} setValue={setValue}/>

                                {/* Display generic/server side errors here */}
                                {data.globalErrors && data.globalErrors.length > 0 && (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="alert alert-danger">
                                                <ul className="m-0 ml-3">
                                                    {data.globalErrors.map(error => {
                                                        return (<li key={error.code}>{error.message}</li>)
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <ReviewPaymentButtons
                                config={config}
                                onCancel={this.props.onCancel}
                                cancelDisabled={!active}
                                onSubmit={stagedFlow ? this.stagePayment : this.submitPayment}
                                submitLabel={(stagedFlow ? messages["continueButton.label"] : messages["otp.makePayment.label"])}
                                submitDisabled={data.paymentInProgress}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ReviewPayment;